import type { Route } from 'next';

import { MediaAudio } from '#components/Media/MediaAudio';
import { MediaLivestream } from '#components/Media/MediaLivestream';
import { MediaVideo } from '#components/Media/MediaVideo';
import type { ArticleTeaser, Image } from '#domains/resolver/board-section-data';
import { logger } from '#utils/logging/logger';

import styles from '../../../../shared/styles/mediacontainer.module.scss';

type Props = {
	publicationDate?: string | null;
	teaserModule: Exclude<ArticleTeaser['media'], Image>;
	articlePath: Route;
	isPriority?: boolean;
	sizes: string;
};

export function BoardModuleMedia({ articlePath, publicationDate, teaserModule, isPriority, sizes }: Props) {
	const thumbnail = teaserModule.thumbnail;

	switch (teaserModule.type) {
		case 'audio':
			if (!teaserModule.url) {
				logger.error(`Board teaser module is of type 'audio', but no audio or url was found in '%s'.`, teaserModule.rowId);

				return null;
			}

			const audioDuration = teaserModule.duration;

			return (
				<div className={`${styles.mediaRoot}`}>
					<MediaAudio
						copyright={teaserModule.copyright}
						duration={audioDuration}
						meta={teaserModule.meta}
						publicationDate={publicationDate}
						thumbnailAltText={thumbnail.altText}
						thumbnailCopyright={thumbnail.copyright}
						thumbnailLink={articlePath}
						thumbnailUrl={thumbnail.url}
						title={teaserModule.title || undefined}
						url={teaserModule.url}
						isPriority={isPriority}
						enableZoomEffect={true}
						sizes={sizes}
						rowId={teaserModule.rowId}
					/>
				</div>
			);

		case 'livestream':
			return (
				<div className={`${styles.mediaRoot}`}>
					<MediaLivestream
						copyright={teaserModule.copyright}
						liveStreamHlsUrl={teaserModule.url ?? null}
						livestreamStartDate={teaserModule.startDate ?? null}
						livestreamEndDate={teaserModule.endDate ?? null}
						thumbnailAltText={thumbnail.altText}
						thumbnailCopyright={thumbnail.copyright}
						thumbnailUrl={thumbnail.url}
						thumbnailLink={articlePath}
						title={teaserModule.title || undefined}
						isPriority={isPriority}
						moduleId={teaserModule.rowId}
						videos={teaserModule.onDemandVideos}
						publicationDate={publicationDate}
						enableZoomEffect={true}
						sizes={sizes}
					/>
				</div>
			);

		case 'video':
			if (!teaserModule.url) {
				logger.error(`Board teaser module is of type 'video', but no video or url was found '%s'.`, teaserModule.rowId);
				return null;
			}

			const videoDuration = teaserModule.duration;

			return (
				<div>
					<MediaVideo
						copyright={teaserModule.copyright}
						duration={videoDuration}
						meta={teaserModule.meta}
						publicationDate={publicationDate}
						thumbnailAltText={thumbnail.altText}
						thumbnailCopyright={thumbnail.copyright}
						thumbnailUrl={thumbnail.url}
						thumbnailLink={articlePath}
						title={teaserModule.title || undefined}
						url={teaserModule.url}
						isPriority={isPriority}
						moduleId={teaserModule.rowId}
						enableZoomEffect={true}
						sizes={sizes}
					/>
				</div>
			);

		default:
			return null;
	}
}
